/**
 * The Go box's rows for what is typed: text matches first, and the server's
 * best guess folded in when the text matched no destination.
 */
import { useMemo } from 'react';

import { buildGoBoxRows } from './goBoxRows';
import { useBestGuess } from './useBestGuess';

import type { GoBoxRow, GoBoxRowOptions } from './goBoxRows';

/**
 * The rows, and whether a guess is still on its way.
 *
 * @param open - Whether the list is showing; a closed list never asks for a guess.
 */
export function useGoBoxRows(
  query: string,
  open: boolean,
  { installedApp, showAsk }: Omit<GoBoxRowOptions, 'guessId'>
): { rows: GoBoxRow[]; guessing: boolean } {
  const textRows = useMemo(
    () => buildGoBoxRows(query, { installedApp, showAsk }),
    [query, installedApp, showAsk]
  );
  const noMatch = !textRows.some(r => r.kind === 'destination');
  const { guessId, guessing } = useBestGuess(query, open && noMatch);
  const rows = useMemo(
    () => (guessId ? buildGoBoxRows(query, { installedApp, showAsk, guessId }) : textRows),
    [guessId, query, installedApp, showAsk, textRows]
  );
  return { rows, guessing };
}
